import { useEffect, useState } from "react";
import { Check, Loader2, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, mediaUrl } from "@/lib/api";

const AiSection = ({ album, onChange }) => {
  const draft = album.theme_draft;
  const run = async (fn) => { try { onChange((await fn()).data); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => {
    if (draft?.status !== "generating") return undefined;
    const t = setInterval(async () => onChange((await api.get(`/albums/${album.id}`)).data), 3000);
    return () => clearInterval(t);
  }, [draft?.status, album.id, onChange]);
  return (
    <div className="mt-5 border-t border-neutral-200 pt-4" data-testid="theme-ai-section">
      <div className="flex items-center gap-2 text-sm font-medium text-neutral-900"><Sparkles size={15} /> AI custom theme</div>
      <p className="text-xs text-neutral-500 mt-1">Creates a unique, subtle background from a few of this album's photos. Each try uses Universal Key balance.</p>
      {!draft && <button className="btn-outline mt-3" onClick={() => run(() => api.post(`/albums/${album.id}/theme/ai`))} data-testid="theme-ai-generate">Create custom theme</button>}
      {draft?.status === "generating" && <p className="text-sm text-neutral-600 mt-3 inline-flex items-center gap-2" data-testid="theme-ai-progress"><Loader2 size={14} className="animate-spin" /> Designing a background… (about 20–40 seconds)</p>}
      {draft?.status === "error" && <p className="text-sm text-red-700 mt-3" data-testid="theme-ai-error">Couldn't create it: {draft.error}</p>}
      {draft?.status === "ready" && <img src={mediaUrl(draft.preview_url)} alt="AI theme preview" className="mt-3 w-full aspect-video object-cover rounded" data-testid="theme-ai-preview" />}
      {draft && draft.status !== "generating" && (
        <div className="flex flex-wrap gap-2 mt-3">
          {draft.status === "ready" && <button className="btn-primary" onClick={() => run(() => api.post(`/albums/${album.id}/theme/ai/accept`))} data-testid="theme-ai-use">Use this</button>}
          <button className="btn-outline" onClick={() => run(() => api.post(`/albums/${album.id}/theme/ai`))} data-testid="theme-ai-retry">Try again</button>
          <button className="btn-outline" onClick={() => run(() => api.delete(`/albums/${album.id}/theme/ai`))} data-testid="theme-ai-cancel">Cancel</button>
        </div>
      )}
    </div>
  );
};

const STYLES = [["corners", "Photo corners"], ["washi", "Washi tape"], ["clean", "Clean"]];

const PageStyle = ({ album, onChange }) => {
  const cur = album.theme?.page_style || "corners";
  const pick = async (style) => {
    try { onChange((await api.patch(`/albums/${album.id}/page-style`, { style })).data); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-5" data-testid="page-style-picker">
      <div className="text-sm font-medium mb-2">Page style</div>
      <div className="dest-switch max-w-sm">
        {STYLES.map(([k, label]) => <button key={k} type="button" className={cur === k ? "on" : ""} onClick={() => pick(k)} data-testid={`page-style-${k}`}>{label}</button>)}
      </div>
    </div>
  );
};

export const ThemeDialog = ({ open, onOpenChange, album, onChange }) => {
  const [themes, setThemes] = useState([]);
  useEffect(() => { if (open && !themes.length) api.get("/themes").then((r) => setThemes(r.data.themes)); }, [open, themes.length]);
  const pick = async (key) => {
    try { onChange((await api.patch(`/albums/${album.id}/theme`, { key })).data); toast.success(key ? "Theme applied" : "Back to automatic"); } catch (e) { toast.error(errMsg(e)); }
  };
  const current = album.theme?.key;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="theme-dialog">
        <DialogHeader>
          <DialogTitle>Album theme</DialogTitle>
          <DialogDescription>{album.theme_manual ? "Chosen by an admin." : `Picked automatically from the photos: ${album.theme?.name}.`}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {themes.map((t) => (
            <button key={t.key} className={`theme-swatch ${current === t.key ? "on" : ""}`} onClick={() => pick(t.key)} data-testid={`theme-option-${t.key}`}>
              {t.bg_thumb ? <img src={mediaUrl(t.bg_thumb)} alt="" loading="lazy" /> : <span className="theme-swatch-empty" />}
              <span style={{ fontFamily: `"${t.font}", serif`, color: t.accent }}>{t.name}</span>
              {current === t.key && <Check size={14} className="theme-swatch-check" />}
            </button>
          ))}
        </div>
        {album.theme_manual && (
          <button className="btn-outline mt-4 inline-flex items-center gap-2 self-start" onClick={() => pick(null)} data-testid="theme-reset-auto"><RotateCcw size={14} /> Reset to automatic</button>
        )}
        <PageStyle album={album} onChange={onChange} />
        <AiSection album={album} onChange={onChange} />
      </DialogContent>
    </Dialog>
  );
};
